<template>
  <div id="paypal-container" :class="{ 'adyen-paypal': showPayPalPayLater && style!.layout === 'horizontal' }" />
  <paypal-payments v-if="showPayPalPayLater" :amount="cart.totals.total" placement="payment" />
</template>

<script lang="ts" setup>
import type { GetPaymentSessionData } from '#root/api/clients/cart/data-contracts'
import type { ProductVariants } from '#root/api/clients/product/data-contracts'
import type { InvoiceData, ShippingAddress } from '#types/cart'
import type { PaypalAddress, PaypalConfiguration } from '#types/paypal'

export type Props = {
  billingAddress?: ShippingAddress
  context?: string
  invoiceAddress?: InvoiceData
  isExpressFlow?: boolean
  isSameAsShipping?: boolean
  locale?: string
  meta?: GetPaymentSessionData['meta']
  selectedVariant?: ProductVariants[number]
}

const {
  billingAddress,
  context,
  invoiceAddress,
  isExpressFlow,
  isSameAsShipping,
  locale = '',
  meta = {
    environment: '',
    clientKey: '',
    brands: [],
    minimumExpiryDate: '',
    type: '',
    hasHolderName: false,
    holderNameRequired: false
  },
  selectedVariant
} = defineProps<Props>()

const { style } = useAppConfig().components.paypal.button
const { paypal } = useAppConfig().checkout
const cart = useCartStore(context)
const { basketId } = storeToRefs(cart)
const cartApi = useApi().cart
const recaptcha = useRecaptcha()
const checkout = useCheckoutStore()
const { billingAddressRef } = storeToRefs(checkout)
const { jsBaseUrl, cssBaseUrl } = useFeatureFlags().configAdyen
const paypalPaymentData = ref()
const paymentInstrumentId = ref()
const isPaypalAddressComplete = ref(false)
const router = useRouter()
const { enableShippingAddressRecaptcha, showPayPalPayLater } = useFeatureFlags()

const addBillingAddress = async () => {
  if (!isExpressFlow) {
    let address

    if (isSameAsShipping) {
      address = {
        ...invoiceAddress,
        firstName: checkout.shippingAddress?.firstName || '',
        lastName: checkout.shippingAddress?.lastName || '',
        country: checkout.shippingAddress?.country || '',
        addressLine1: checkout.shippingAddress?.addressLine1 || '',
        postalCode: checkout.shippingAddress?.postalCode || '',
        city: checkout.shippingAddress?.city || '',
        phone: checkout.shippingAddress?.phone || '',
        email: checkout.shippingAddress?.email || '',
        addressId: checkout.shippingAddress?.addressId || '',
        subscription: checkout.shippingAddress?.subscriptions || {},
        province: checkout.shippingAddress?.province || ''
      }
    }
    else {
      address = { ...billingAddress, ...invoiceAddress }
    }

    if (address)
      await cart.addBillingAddress(address)
  }
}

// load Adyen JS libraries
const loadAdyenScript = async () => {
  return new Promise((resolve, reject) => {
    try {
      // need to check it here because if it exists useHead won't return any promise
      if (
        document.querySelectorAll(`script[src="${jsBaseUrl}"]`).length > 0
        && document.querySelectorAll(`link[href="${cssBaseUrl}"]`).length > 0
      ) {
        // Ayden scripts already loaded
        resolve(true)
        return
      }

      useHead({
        link: () => [
          // load the Adyen CSS library which functions the secure credit card form
          {
            rel: 'stylesheet',
            type: 'text/css',
            href: cssBaseUrl,
            integrity: 'sha384-zgFNrGzbwuX5qJLys75cOUIGru/BoEzhGMyC07I3OSdHqXuhUfoDPVG03G+61oF4',
            crossorigin: 'anonymous'
          }

        ],
        script: [
          // load the Adyen JS library which functions the secure credit card form
          {
            src: jsBaseUrl,
            integrity: 'sha384-O8p0CLZyOw1jkmYN7ZwJxWzd+sDYRFGpLEffqc+dKye24gFImbU72did4PC7ysTY',
            crossorigin: 'anonymous',
            onload: async () => {
              resolve(true)
            },
            onerror: (err) => {
              // Adyen script loading failed
              reject(err)
            }
          },
        ]
      }, { mode: 'client' })
    }
    catch (error) {
      console.error('Adyen script loading failed', error)
      reject(error)
    }
  })
}

const getPaypalInstance = () => {
  return window.AdyenCheckout({
    locale,
    environment: meta.environment,
    clientKey: meta.clientKey
  })
}

const onSubmit = async (state, component) => {
  if (context === 'paypalPdp' && selectedVariant) {
    await cart.add({
      product: selectedVariant.id,
      upc: selectedVariant.upc,
      suppressMiniCart: true,
      suppressNotification: true,
      maxQty: 1
    })
  }
  const response = await cart.postPaymentInstruments('PAYPAL', { additionalDetails: { paymentData: JSON.stringify(state.data) } })
  const paymentInstrument = response.payment_instruments.find((payment_instrument) => payment_instrument.payment_method_id === 'PAYPAL')
  component.handleAction(JSON.parse(paymentInstrument.c_requestID))
  paymentInstrumentId.value = paymentInstrument.payment_instrument_id
  return response
}

const isShippingAddressIncomplete = (address) => {
  const fieldsToSkip = [
    'id',
    'addressId',
    'addressLine2',
    'addressLine3',
    'stateCode',
    'subscriptions',
  ]
  return Object.keys(address).some((field) => {
    if (fieldsToSkip.includes(field)) return false
    return !address[field]
  })
}

const mapAddressData = (address: PaypalAddress) => {
  const [addressLine1, addressLine2] = address.street?.split(',') || []
  const stateCode = address.state?.toUpperCase() || address.stateOrProvince?.toUpperCase() || ''

  return {
    addressId: '',
    firstName: address.firstName || '',
    lastName: address.lastName || '',
    email: address.shopperEmail || '',
    country: address.countryCode?.toUpperCase() || address.country?.toUpperCase() || '',
    addressLine1: addressLine1 || '',
    addressLine2: addressLine2 || '',
    postalCode: address.postalCode || '',
    city: address.city || '',
    province: stateCode,
    phone: address.telephoneNumber || '',
    stateCode,
    countryCode: address.countryCode?.toUpperCase() || address.country?.toUpperCase() || '',
    region: stateCode
  }
}

let paypalConfiguration: PaypalConfiguration = {
  style,
  countryCode: useCountryCode(),
  amount: {
    currency: cart.currency,
    value: cart.totals.total
  },
  ...paypal[useLocale()].config,
  blockPayPalCreditButton: true,
  blockPayPalPayLaterButton: !showPayPalPayLater,
  blockPayPalVenmoButton: true,
  showPayButton: true,
  isExpress: isExpressFlow,
  locale // This is needed for translating PayPal button labels it has to be in en_US format
}

if (!isExpressFlow) {
  paypalConfiguration = {
    ...paypalConfiguration,
    onSubmit: async (state, component) => {
      await addBillingAddress()
      const response = await onSubmit(state, component)
      const stateCode = response.billingAddress?.province || ''
      billingAddressRef.value = {
        ...response.billingAddress,
        invoiceRequested: invoiceAddress?.invoiceRequested,
        stateCode,
        region: stateCode
      }
    },
    onAdditionalDetails: async (state) => {
      await checkout.prepareOrder()

      checkout.placeOrder({
        code: 'PAYPAL',
        id: 'adyen',
        additionalData: [
          {
            c_paymentID: '',
            c_payerID: JSON.stringify(state.data),
            c_requestID: '',
            payment_instrument_id: paymentInstrumentId.value,
            payment_method_id: 'PAYPAL'
          }
        ]
      }, recaptcha)
    }
  }
}
else {
  paypalConfiguration = {
    ...paypalConfiguration,
    onShopperDetails: async (shopperDetails, rawData, actions) => {
      const { billingAddress, shippingAddress, shopperEmail, shopperName, telephoneNumber } = shopperDetails
      // Create Shipping Object from paypal
      const createShippingObj = {
        ...shippingAddress,
        shopperEmail,
        ...shopperName,
        telephoneNumber
      }

      const paypalShippingAddress = mapAddressData(createShippingObj)
      isPaypalAddressComplete.value = !isShippingAddressIncomplete(paypalShippingAddress)
      try {
        // Set Shipping Address
        const captchaResponse = enableShippingAddressRecaptcha ? await recaptcha.execute('login') : ''
        const response = await cartApi.$addShippingAddress(basketId.value, {
          ...paypalShippingAddress,
          subscriptions: {
            newsletterConsent: false
          },
          ...captchaResponse && { recaptcha_response: captchaResponse }
        }, { context: 'PAYPAL_EXPRESS' })
        // Set payment data
        const paymentInstrument = response.payment_instruments.find((payment_instrument) => payment_instrument.payment_method_id === 'PAYPAL')
        const c_requestID = JSON.parse(paymentInstrument.c_requestID)
        paypalPaymentData.value = c_requestID
      }
      catch (e) {
        console.log('Error adding shipping address', e)
      }

      // Create Billing Object from paypal
      const createBillingObj = {
        ...billingAddress,
        shopperEmail,
        ...shopperName,
        telephoneNumber
      }
      const newBillingAddress = mapAddressData(createBillingObj)

      billingAddressRef.value = { ...newBillingAddress }
      await cart.addBillingAddress(newBillingAddress)

      actions.resolve()
    },
    onShippingAddressChange: async (data, _actions, component) => {
      const captchaResponse = enableShippingAddressRecaptcha ? await recaptcha.execute('login') : ''
      const response = await cartApi.$addShippingAddress(basketId.value, {
        ...mapAddressData(data.shippingAddress),
        subscriptions: {
          newsletterConsent: false
        },
        ...captchaResponse && { recaptcha_response: captchaResponse }
      }, { context: 'PAYPAL_EXPRESS' })

      const paymentInstrument = response.payment_instruments.find((payment_instrument) => payment_instrument.payment_method_id === 'PAYPAL')
      const c_requestID = JSON.parse(paymentInstrument.c_requestID)
      component.updatePaymentData(c_requestID.paymentData)
    },
    onSubmit,
    onAdditionalDetails: async (state, component) => {
      if (isPaypalAddressComplete.value) {
        // Update paypals payment data
        component.updatePaymentData(paypalPaymentData.value)

        await checkout.prepareOrder()

        checkout.placeOrder({
          code: 'PAYPAL',
          id: 'adyen',
          additionalData: [
            {
              c_paymentID: '',
              c_payerID: JSON.stringify(state.data),
              c_requestID: '',
              payment_instrument_id: paymentInstrumentId.value,
              payment_method_id: 'PAYPAL'
            }
          ]
        }, recaptcha)
      }
      else {
        // If the address is incomplete navigate the user to shipping page
        router.push(useLocalisedRoute('/checkout'))
      }
    }
  }
}

const adyenCheckout = async () => {
  const checkout = await getPaypalInstance()
  checkout.create('paypal', paypalConfiguration).mount('#paypal-container')
}

onMounted(() => {
  loadAdyenScript()
    .then(async () => {
      await adyenCheckout()
    })
})
</script>
